import React, {useState} from "react";
import {Button, DatePicker, Form, Input, Modal, Popconfirm, Select, Space, Table, App} from "antd";
import {useQuery} from "react-query";
import {useIntl} from "react-intl";
import jwtAxios from "../../../../auth/jwt-api";
import useDebounce from "../../../../utils/useDebounce";
import {DeleteOutlined, EditOutlined} from "@ant-design/icons";
import {FaPlus} from "react-icons/fa";
import dayjs from "dayjs";

export default function Signatory() {
    const {formatMessage} = useIntl();
    const {message} = App.useApp();
    const t = (id) => formatMessage({id});

    const [pagination, setPagination] = useState({current: 1, pageSize: 10});
    const [searchName, setSearchName] = useState("");
    const [searchPosition, setSearchPosition] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const debouncedSetName = useDebounce((value) => {
        setSearchName(value);
        setPagination((prev) => ({...prev, current: 1}));
    }, 500);

    const debouncedSetPosition = useDebounce((value) => {
        setSearchPosition(value);
        setPagination((prev) => ({...prev, current: 1}));
    }, 500);

    const {data: usersData} = useQuery(
        ["/user"],
        () => jwtAxios.get("/user", {params: {limit: 1000}}),
    );
    const users = usersData?.data?.data?.results?.rows ?? [];

    const {data, isLoading, refetch} = useQuery(
        ["/signatory", pagination.current, pagination.pageSize, searchName, searchPosition],
        () =>
            jwtAxios.get("/signatory", {
                params: {
                    page: pagination.current,
                    limit: pagination.pageSize,
                    order: "id",
                    sortBy: "DESC",
                    ...(searchName && {fullName: searchName}),
                    ...(searchPosition && {position: searchPosition}),
                },
            }),
        {keepPreviousData: true}
    );

    const rows = data?.data?.data?.results?.rows ?? [];
    const total = data?.data?.data?.results?.count ?? 0;

    const handleAdd = () => {
        setEditingRecord(null);
        form.resetFields();
        setIsModalOpen(true);
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        form.setFieldsValue({
            ...record,
            userId: record.userId || undefined,
            birthDate: record.birthDate ? dayjs(record.birthDate) : null,
            proxyDate: record.proxyDate ? dayjs(record.proxyDate) : null,
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        try {
            await jwtAxios.delete(`/signatory/${id}`);
            message.success(t("notification.success.delete"));
            refetch();
        } catch (error) {
            message.error(error.response?.data?.error || t("notification.error.delete"));
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const payload = {
                ...values,
                birthDate: values.birthDate ? values.birthDate.format("YYYY-MM-DD") : undefined,
                proxyDate: values.proxyDate ? values.proxyDate.format("YYYY-MM-DD") : undefined,
            };

            if (editingRecord) {
                await jwtAxios.patch(`/signatory/${editingRecord.id}`, payload);
            } else {
                await jwtAxios.post("/signatory", payload);
            }
            setIsModalOpen(false);
            form.resetFields();
            setEditingRecord(null);
            refetch();
        } catch (error) {
            message.error(error.response?.data?.error || t("message.error.occurred"));
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        form.resetFields();
        setEditingRecord(null);
    };

    const columns = [
        {
            title: "№",
            key: "index",
            width: 60,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: t("signatory.field.fullName"),
            dataIndex: "fullName",
            key: "fullName",
        },
        {
            title: t("signatory.field.position"),
            dataIndex: "position",
            key: "position",
        },
        {
            title: t("signatory.field.pinfl"),
            dataIndex: "pinfl",
            key: "pinfl",
        },
        {
            title: t("signatory.field.proxyId"),
            dataIndex: "proxyId",
            key: "proxyId",
        },
        {
            title: t("signatory.field.birthDate"),
            dataIndex: "birthDate",
            key: "birthDate",
            render: (value) => value ? dayjs(value).format("DD.MM.YYYY") : "-",
        },
        {
            title: t("signatory.field.proxyDate"),
            dataIndex: "proxyDate",
            key: "proxyDate",
            render: (value) => value ? dayjs(value).format("DD.MM.YYYY") : "-",
        },
        {
            title: t("signatory.field.authorityBasis"),
            dataIndex: "authorityBasis",
            key: "authorityBasis",
            render: (value) => value ? t(`signatory.authorityBasis.${value}`) : "-",
        },
        {
            title: t("table.column.actions"),
            key: "actions",
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined/>} onClick={() => handleEdit(record)}/>
                    <Popconfirm
                        title={t("confirm.delete")}
                        onConfirm={() => handleDelete(record.id)}
                        okText={t("common.yes")}
                        cancelText={t("common.no")}
                    >
                        <Button danger icon={<DeleteOutlined/>}/>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{padding: "clamp(10px,3%,30px)"}}>
            <h1>{t("signatory.page.title")}</h1>
            <br/>
            <Space style={{marginBottom: 16, width: "100%", justifyContent: "space-between"}} wrap>
                <Space wrap>
                    <div>
                        <label>{t("signatory.field.fullName")}</label>
                        <Input
                            placeholder={t("signatory.field.fullName")}
                            allowClear
                            onChange={(e) => debouncedSetName(e.target.value)}
                            style={{width: 250}}
                        />
                    </div>
                    <div>
                        <label>{t("signatory.field.position")}</label>
                        <Input
                            placeholder={t("signatory.field.position")}
                            allowClear
                            onChange={(e) => debouncedSetPosition(e.target.value)}
                            style={{width: 250}}
                        />
                    </div>
                </Space>
                <Button type="primary" icon={<FaPlus/>} onClick={handleAdd}>
                    {t("button.add")}
                </Button>
            </Space>
            <Table
                rowKey="id"
                loading={isLoading}
                columns={columns}
                dataSource={rows}
                scroll={{x: "max-content"}}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                }}
                onChange={(p) => setPagination({current: p.current, pageSize: p.pageSize})}
            />

            <Modal
                title={editingRecord ? t("signatory.edit") : t("signatory.add")}
                open={isModalOpen}
                onCancel={handleCancel}
                footer={null}
                centered
                destroyOnClose
            >
                <br/>
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        label={t("signatory.field.user")}
                        name="userId"
                    >
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            placeholder={t("signatory.field.user")}
                        >
                            {users.map((user) => (
                                <Select.Option key={user.id} value={user.id}>
                                    {user.username}
                                </Select.Option>
                            ))}
                       
                       
                       
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label={t("signatory.field.fullName")}
                        name="fullName"
                        rules={[{required: true, message: t("signatory.error.fullName_required")}]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        label={t("signatory.field.position")}
                        name="position"
                        rules={[{required: true, message: t("signatory.error.position_required")}]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item label={t("signatory.field.pinfl")} name="pinfl">
                        <Input maxLength={14}/>
                    </Form.Item>
                    <Form.Item label={t("signatory.field.proxyId")} name="proxyId">
                        <Input/>
                    </Form.Item>
                    <Form.Item label={t("signatory.field.birthDate")} name="birthDate">
                        <DatePicker style={{width: "100%"}} format="DD.MM.YYYY"/>
                    </Form.Item>
                    <Form.Item label={t("signatory.field.proxyDate")} name="proxyDate">
                        <DatePicker style={{width: "100%"}} format="DD.MM.YYYY"/>
                    </Form.Item>
                    <Form.Item label={t("signatory.field.authorityBasis")} name="authorityBasis">
                        <Select allowClear>
                            <Select.Option value="power_of_attorney">{t("signatory.authorityBasis.power_of_attorney")}</Select.Option>
                            <Select.Option value="charter">{t("signatory.authorityBasis.charter")}</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item style={{marginBottom: 0, textAlign: "right"}}>
                        <Space>
                            <Button onClick={handleCancel}>{t("button.cancel")}</Button>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                {t("button.submit")}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}